/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/services/authService.js

// Production'da environment variable'dan al, yoksa localhost kullan
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Token yalnızca HttpOnly cookie'de yaşar — localStorage/sessionStorage kullanılmaz
// API çağrılarında credentials: "include" ile cookie otomatik gönderilir

const purgeLegacyTokenStorage = () => {
  try {
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
  } catch {
    // storage erişimi yoksa sorun değil
  }
};

const removeToken = () => {
  purgeLegacyTokenStorage();
  clearUserCache();
};

// ✅ User cache - Duplicate API çağrılarını önlemek için
let userCache = null;
let pendingUserRequest = null;
const CACHE_DURATION = 2 * 60 * 1000; // 2 dakika cache süresi (güvenlik için kısaltıldı)
let cacheTimestamp = null;

// ✅ Cache'i temizle (export edildi - gerekirse dışarıdan çağrılabilir)
export const clearUserCache = () => {
  userCache = null;
  pendingUserRequest = null;
  cacheTimestamp = null;
};

// ✅ Login - Backend cookie set ediyor (HttpOnly)
export const login = async (username, password) => {
  const response = await fetch(`${API_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
    credentials: "include", // Cookie'yi gönder ve al
  });

  const data = await response.json();

  if (!response.ok) {
    let errorMsg = "Kullanıcı adı veya şifre yanlış.";
    if (data.detail) {
      if (Array.isArray(data.detail)) errorMsg = data.detail.map((d) => d.msg).join(" - ");
      else if (typeof data.detail === "string") errorMsg = data.detail;
      else if (typeof data.detail === "object") errorMsg = JSON.stringify(data.detail);
    }
    throw new Error(errorMsg);
  }

  // Token HttpOnly cookie'de; body'de access_token dönmez
  purgeLegacyTokenStorage();
  clearUserCache();

  return data;
};

// ✅ Logout - Backend cookie'yi siliyor
export const logout = async () => {
  // Cookie otomatik gönderilir (credentials: "include" ile)
  await fetch(`${API_URL}/auth/logout`, {
    method: "POST",
    credentials: "include", // Cookie'yi gönder
  });
  
  removeToken(); // Cache'i temizle
};

// ✅ Me - Kullanıcı bilgisi almak için (Cache ile optimize edilmiş)
// Cookie otomatik gönderilir (credentials: "include" ile)
export const getCurrentUser = async (forceRefresh = false) => {
  // Cache kontrolü - Eğer cache varsa ve süresi dolmamışsa, cache'den dön
  const now = Date.now();
  if (
    !forceRefresh &&
    userCache &&
    cacheTimestamp &&
    (now - cacheTimestamp) < CACHE_DURATION
  ) {
    return userCache;
  }

  // Eğer zaten bir request pending ise, onu bekle (duplicate request'leri önle)
  if (pendingUserRequest) {
    return pendingUserRequest;
  }

  // Yeni request oluştur - Cookie otomatik gönderilir (HttpOnly)
  // Backend cookie'den okuyor, Authorization header'a gerek yok
  pendingUserRequest = fetch(`${API_URL}/auth/me`, {
    method: "GET",
    credentials: "include", // Cookie'yi gönder (HttpOnly cookie otomatik gönderilir)
  })
    .then(async (response) => {
      if (!response.ok) {
        // 401 durumunda token geçersiz, temizle
        if (response.status === 401) {
          removeToken(); // Token'ı da temizle
          clearUserCache(); // User cache'i temizle
          
          // Login sayfasındayken redirect yapma (sonsuz döngü önleme)
          const isLoginPage = window.location.hash === '#/login' || window.location.pathname === '/login';
          if (!isLoginPage) {
            window.location.href = "/login";
          }
          // 401 için özel error mesajı
          const error = new Error("Oturum süresi doldu");
          error.status = 401;
          throw error;
        } else {
          // 401 olmayan hatalar için generic error (UserContext bunu unauthorized olarak algılamamalı)
          clearUserCache(); // User cache'i temizle
          const error = new Error(`Sunucu hatası: ${response.status}`);
          error.status = response.status;
          throw error;
        }
      }

      const userData = await response.json();
      
      // Cache'e kaydet
      userCache = userData;
      cacheTimestamp = Date.now();
      
      return userData;
    })
    .finally(() => {
      // Request tamamlandıktan sonra pending request'i temizle
      pendingUserRequest = null;
    });

  return pendingUserRequest;
};

// ✅ API çağrıları için yardımcı fonksiyon - Cookie otomatik gönderilir (HttpOnly)
export const fetchWithAuth = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    credentials: "include", // Cookie'yi gönder (HttpOnly cookie otomatik gönderilir)
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (response.status === 401) {
    removeToken();
    window.location.href = "/login";
    throw new Error("Oturum süresi doldu");
  }

  return response;
};
